import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ISliderWidget } from 'types/widgets';
import { getWidgetGeometry } from 'providers/creative/prepare-creative/recalculate-positions';
import styles from './slider.module.css';
import { useExternalControl } from '../../../../hooks';

interface IPropTypes {
  desc: ISliderWidget;
}

const Slider: React.FC<IPropTypes> = ({ desc }) => {
  const {
    id,
    formId,
    initialValue,
    minValue,
    maxValue,
    labelTextColor,
    labelSuffix,
    internalSizes,
    width,
    height,
  } = desc;

  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState(initialValue);

  const progress = useMemo(
    () => ((value - minValue) / (maxValue - minValue)) * 100,
    [value, minValue, maxValue],
  );

  const thumbSize = useMemo(() => {
    const geometry = getWidgetGeometry(
      { width: internalSizes.thumb.size, height: internalSizes.thumb.size },
      width,
      height,
    );

    return geometry.relativeSize.width.value;
  }, [width, height, internalSizes.thumb]);

  const isLabelEnabled = useMemo(
    () => Boolean(labelTextColor || internalSizes.label),
    [labelTextColor, internalSizes.label],
  );

  const onInputChange = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      setValue(Number(target.value));
    },
    [],
  );
  const externalAttributes = useMemo(() => ['type', 'min', 'max', 'value'], []);

  const handlers = useMemo(
    () => ({
      onInput: onInputChange,
    }),
    [onInputChange],
  );

  useExternalControl(inputRef, handlers, externalAttributes);

  return (
    <section
      id={id}
      className={styles.slider}
      style={{ '--thumb-size': `${thumbSize}%` }}
    >
      <div className={styles.trackContainer} data-role="trackContainer">
        <div
          className={styles.thumb}
          data-role="thumb"
          style={{ left: `calc(${progress}%)` }}
        />

        <div className={styles.track} data-role="track">
          <div
            className={styles.progress}
            data-role="progress"
            style={{ width: `${progress}%` }}
          />
        </div>
        <input
          ref={inputRef}
          id={`input-${id}`}
          name={id}
          form={formId}
          className={styles.input}
          data-role="input"
          type="range"
          min={minValue}
          max={maxValue}
          value={value}
          onInput={onInputChange}
        />
      </div>

      {isLabelEnabled && (
        <label className={styles.label}>
          <span className={styles.labelSpacer}>100%</span>
          <span className={styles.labelValue}>
            {value}
            {labelSuffix}
          </span>
        </label>
      )}
    </section>
  );
};

export default React.memo(Slider);
